/**
 * AuthGate — connects Clerk authentication to tracker_users in PocketBase.
 *
 * Sits inside UsersProvider + UserViewProvider. When a Clerk user signs in:
 * 1. Looks up their clerkId in the loaded tracker_users list
 * 2. If found → auto-sets activeView to that user and renders children
 * 3. If not found → attempts to auto-link by matching Clerk email to a tracker_user
 * 4. If auto-link fails → shows onboarding screen (invite code or request access)
 * 5. If not signed in → shows sign-in prompt
 */

import { useState, useEffect, useRef } from 'react';
import { useAuth, useUser, useClerk, SignIn, SignUp } from '@clerk/react';
import { useUsers } from '../hooks/useUsers.jsx';
import { useUserView } from '../hooks/useUserView.jsx';
import { CurrentUserProvider } from '../hooks/useCurrentUser.jsx';
import { PodProvider, usePod } from '../hooks/usePod.jsx';
import { UserViewProvider } from '../hooks/useUserView.jsx';
import { EmojiDropdown } from '@code-burrow/design-system';

function getApiBase() {
    const proxyUrl = typeof import.meta !== 'undefined' && import.meta.env?.VITE_PROXY_URL;
    return proxyUrl ? `${proxyUrl.replace(/\/+$/, '')}/api` : '/api';
}

const EMOJI_OPTIONS = ['🐻', '🦊', '🐱', '🐶', '🐼', '🦉', '🐸', '🐧', '🦋', '🐝', '🦎', '🐙'];

/**
 * ViewOptionsSync — re-wraps children with UserViewProvider once pod context is available.
 * This inner provider overrides the outer (App-level) one with pod-aware view options.
 */
function ViewOptionsSync({ currentUserName, children }) {
    const { allMembers } = usePod();
    const podMemberNames = allMembers?.map(m => m.userName).filter(Boolean) || [];
    return (
        <UserViewProvider currentUserName={currentUserName} podMemberNames={podMemberNames}>
            {children}
        </UserViewProvider>
    );
}

/**
 * SignedOutScreen — shown when no user is signed in.
 * Renders Clerk's SignIn/SignUp cards inline so the background stays visible.
 * Uses URL hash to toggle between sign-in and sign-up without leaving the page.
 */
function SignedOutScreen({ background }) {
    const [mode, setMode] = useState(() =>
        window.location.hash.includes('sign-up') ? 'signUp' : 'signIn'
    );

    useEffect(() => {
        const onHash = () => {
            setMode(window.location.hash.includes('sign-up') ? 'signUp' : 'signIn');
        };
        window.addEventListener('hashchange', onHash);
        return () => window.removeEventListener('hashchange', onHash);
    }, []);

    return (
        <div className="relative min-h-screen overflow-hidden flex items-center justify-center">
            {background || <div className="absolute inset-0 bg-bg-primary" />}
            <div className="relative z-10">
                {mode === 'signUp' ? (
                    <SignUp routing="virtual" signInUrl="#sign-in" />
                ) : (
                    <SignIn routing="virtual" signUpUrl="#sign-up" />
                )}
            </div>
        </div>
    );
}

/**
 * OnboardingScreen — shown when a signed-in Clerk user has no tracker_user.
 * Two paths: enter invite code (provisions account + joins pod), or request access.
 */
function OnboardingScreen({ clerkUser, clerkId, getToken, signOut, linkError }) {
    const defaultName = clerkUser?.firstName || '';

    const [displayName, setDisplayName] = useState(defaultName);
    const [emoji, setEmoji] = useState(EMOJI_OPTIONS[Math.floor(Math.random() * EMOJI_OPTIONS.length)]);
    const [inviteCode, setInviteCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(linkError || null);
    const [showCustomEmoji, setShowCustomEmoji] = useState(false);
    const [customEmojiInput, setCustomEmojiInput] = useState('');
    const [requestSent, setRequestSent] = useState(false);
    const handleProvision = async () => {
        if (!displayName.trim()) return;
        setBusy(true);
        setError(null);
        try {
            const token = await getToken();
            const apiBase = getApiBase();
            const res = await fetch(`${apiBase}/auth/provision`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({
                    displayName: displayName.trim(),
                    emoji,
                    inviteCode: inviteCode.trim() || undefined,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.error || `Failed: ${res.status}`);
            }
            // Reload so AuthGate picks up the new tracker_user
            window.location.reload();
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const handleRequestAccess = async () => {
        if (!displayName.trim()) return;
        setBusy(true);
        setError(null);
        try {
            const token = await getToken();
            const apiBase = getApiBase();
            const res = await fetch(`${apiBase}/access-requests`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({
                    name: displayName.trim(),
                    email: clerkUser.emailAddresses[0]?.emailAddress,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.error || `Failed: ${res.status}`);
            }
            setRequestSent(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    if (requestSent) {
        return (
            <div className="min-h-screen bg-bg-primary flex items-center justify-center">
                <div className="bg-surface rounded-lg p-8 max-w-sm w-full text-center">
                    <div className="text-4xl mb-4">✨</div>
                    <h2 className="text-xl font-bold font-display text-text-primary mb-2">
                        Request Sent!
                    </h2>
                    <p className="text-sm text-text-secondary font-body mb-6">
                        We've let the group know. They'll send you an invite code when they're ready.
                    </p>
                    <button
                        onClick={() => signOut()}
                        className="w-full px-4 py-2.5 rounded-md text-text-tertiary text-sm hover:text-text-secondary transition-all"
                    >
                        Sign Out
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-bg-primary flex items-center justify-center">
            <div className="bg-surface rounded-lg p-8 max-w-sm w-full">
                <div className="text-center mb-6">
                    <h2 className="text-xl font-bold font-display text-text-primary mb-1">
                        Welcome, {defaultName || 'friend'}!
                    </h2>
                    <p className="text-sm text-text-secondary font-body">
                        Set up your tracker profile to get started.
                    </p>
                </div>

                {error && (
                    <div className="mb-4 p-3 rounded-md bg-red-500/10 border border-red-500/20 text-sm text-red-400">
                        {error}
                    </div>
                )}

                {/* Display name */}
                <label className="block mb-1 text-xs text-text-tertiary font-body">Display Name</label>
                <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Your name"
                    maxLength={50}
                    className="w-full px-3 py-2 rounded-md bg-bg-primary border border-border text-text-primary text-sm placeholder:text-text-tertiary focus:outline-none focus:border-accent-primary focus:ring-1 focus:ring-accent-primary mb-4"
                />

                {/* Emoji picker */}
                <label className="block mb-1 text-xs text-text-tertiary font-body">Your Avatar</label>
                <div className="flex items-center gap-2 mb-4">
                    <EmojiDropdown value={emoji} onChange={setEmoji} placeholder="Pick emoji" />
                    {showCustomEmoji ? (
                        <input
                            type="text"
                            value={customEmojiInput}
                            onChange={(e) => {
                                const val = e.target.value;
                                setCustomEmojiInput(val);
                                const emojiMatch = val.match(/\p{Emoji_Presentation}|\p{Emoji}\uFE0F/gu);
                                if (emojiMatch) {
                                    setEmoji(emojiMatch[emojiMatch.length - 1]);
                                }
                            }}
                            placeholder="Type emoji"
                            autoFocus
                            className="w-20 h-9 rounded-md text-center text-lg bg-bg-primary border border-accent-primary text-text-primary focus:outline-none"
                        />
                    ) : (
                        <button
                            onClick={() => setShowCustomEmoji(true)}
                            className="h-9 px-2.5 rounded-full border border-dashed border-border-subtle text-sm text-text-tertiary hover:text-text-secondary transition-colors"
                            title="Choose your own emoji"
                        >
                            Custom
                        </button>
                    )}
                </div>

                {/* Invite code */}
                <label className="block mb-1 text-xs text-text-tertiary font-body">Invite Code (optional)</label>
                <input
                    type="text"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                    placeholder="e.g. ABC123"
                    maxLength={10}
                    className="w-full px-3 py-2 rounded-md bg-bg-primary border border-border text-text-primary text-sm placeholder:text-text-tertiary focus:outline-none focus:border-accent-primary focus:ring-1 focus:ring-accent-primary mb-6 font-mono tracking-wider"
                />

                {/* Actions */}
                <button
                    onClick={handleProvision}
                    disabled={busy || !displayName.trim()}
                    className="w-full px-4 py-2.5 rounded-md bg-accent-primary text-text-inverse text-sm font-medium hover:brightness-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed mb-3"
                >
                    {busy ? 'Setting up...' : inviteCode.trim() ? 'Join Pod & Start Tracking' : 'Start Tracking Solo'}
                </button>

                <div className="flex items-center gap-3 mb-3">
                    <div className="flex-1 h-px bg-border-subtle" />
                    <span className="text-xs text-text-tertiary">or</span>
                    <div className="flex-1 h-px bg-border-subtle" />
                </div>

                <button
                    onClick={handleRequestAccess}
                    disabled={busy || !displayName.trim()}
                    className="w-full px-4 py-2.5 rounded-md bg-bg-secondary border border-border-subtle text-text-primary text-sm font-medium hover:bg-bg-primary transition-all disabled:opacity-50 disabled:cursor-not-allowed mb-3"
                >
                    {busy ? 'Sending...' : 'Request Access'}
                </button>

                <button
                    onClick={() => signOut()}
                    className="w-full px-4 py-2.5 rounded-md text-text-tertiary text-sm hover:text-text-secondary transition-all"
                >
                    Sign Out
                </button>
            </div>
        </div>
    );
}

/**
 * Dev-only bypass: set VITE_DEV_BYPASS_AUTH=true to skip Clerk and use the
 * first tracker_user. Useful for headless preview testing.
 */
function DevAuthBypass({ children }) {
    const { users, loading } = useUsers();
    if (loading || users.length === 0) {
        return (
            <div className="min-h-screen bg-bg-primary flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-text-tertiary border-t-accent-primary rounded-full animate-spin" />
            </div>
        );
    }
    // Use VITE_DEV_USER env var to pick the dev user, or fall back to first user
    const devUserName = import.meta.env.VITE_DEV_USER;
    const devUser = (devUserName && users.find(u => u.name === devUserName)) || users[0];
    return (
        <CurrentUserProvider user={devUser}>
            <PodProvider>
                <ViewOptionsSync currentUserName={devUser.name}>
                    {children}
                </ViewOptionsSync>
            </PodProvider>
        </CurrentUserProvider>
    );
}

export default function AuthGate({ children, background }) {
    if (import.meta.env.VITE_DEV_BYPASS_AUTH === 'true') {
        return <DevAuthBypass>{children}</DevAuthBypass>;
    }

    return <ClerkAuthGate background={background}>{children}</ClerkAuthGate>;
}

function ClerkAuthGate({ children, background }) {
    const { isLoaded: authLoaded, isSignedIn, userId: clerkId, getToken } = useAuth();
    const { user: clerkUser } = useUser();
    const { signOut } = useClerk();
    const { users, loading: usersLoading } = useUsers();
    const { setActiveView } = useUserView();

    // 'checking' | 'linked' | 'linking' | 'no-match' | 'signed-out'
    const [linkState, setLinkState] = useState('checking');
    const [linkError, setLinkError] = useState(null);
    const linkedRef = useRef(false);

    useEffect(() => {
        if (!authLoaded || usersLoading) return;

        if (!isSignedIn) {
            setLinkState('signed-out');
            return;
        }

        // Check if any tracker_user already has this clerkId
        const matched = users.find((u) => u.clerkId === clerkId);
        if (matched) {
            if (!linkedRef.current) {
                setActiveView(matched.name);
                linkedRef.current = true;
            }
            setLinkState('linked');
            return;
        }

        // No clerkId match — try to auto-link by matching Clerk name to a tracker_user.
        // This handles Clerk dev/prod environments having different user IDs:
        // the clerkId on the tracker_user may be stale, but the name still matches.
        const clerkName = clerkUser?.firstName?.toLowerCase();
        const clerkEmail = clerkUser?.emailAddresses?.[0]?.emailAddress?.toLowerCase();

        const candidate = users.find((u) => {
            const nameMatch = clerkName && (
                u.name.toLowerCase() === clerkName ||
                u.displayName?.toLowerCase() === clerkName
            );
            const emailMatch = clerkEmail && u.email?.toLowerCase() === clerkEmail;
            return nameMatch || emailMatch;
        });

        if (candidate) {
            // Auto-link this tracker_user to the Clerk account via proxy server
            setLinkState('linking');
            (async () => {
                try {
                    const token = await getToken();
                    const apiBase = getApiBase();
                    const res = await fetch(`${apiBase}/auth/link`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                        },
                        body: JSON.stringify({ trackerUserId: candidate.id }),
                    });
                    if (!res.ok) {
                        const data = await res.json().catch(() => ({}));
                        throw new Error(data.error || `Link failed: ${res.status}`);
                    }
                    setActiveView(candidate.name);
                    linkedRef.current = true;
                    setLinkState('linked');
                    window.location.reload(); // Re-fetch users with updated clerkId
                } catch (err) {
                    console.error('Auto-link failed:', err);
                    setLinkError(`Failed to link account: ${err.message}`);
                    setLinkState('no-match');
                }
            })();
        } else {
            setLinkState('no-match');
        }
    }, [authLoaded, isSignedIn, clerkId, clerkUser, users, usersLoading, getToken]);

    // Loading state
    if (!authLoaded || usersLoading || linkState === 'checking' || linkState === 'linking') {
        return (
            <div className="min-h-screen bg-bg-primary flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-text-tertiary border-t-accent-primary rounded-full animate-spin" />
            </div>
        );
    }

    // Not signed in — auto-open Clerk modal, show background while it loads
    if (linkState === 'signed-out') {
        return <SignedOutScreen background={background} />;
    }

    // Signed in but no matching tracker_user — show onboarding
    if (linkState === 'no-match') {
        return (
            <OnboardingScreen
                clerkUser={clerkUser}
                clerkId={clerkId}
                getToken={getToken}
                signOut={signOut}
                linkError={linkError}
            />
        );
    }

    // Linked — resolve the current tracker_user and wrap with providers
    const currentUser = users.find((u) => u.clerkId === clerkId) || null;

    if (!currentUser) {
        // Safety net — linkState is 'linked' but user disappeared
        return (
            <div className="min-h-screen bg-bg-primary flex items-center justify-center">
                <div className="bg-surface rounded-lg p-8 max-w-sm text-center">
                    <p className="text-sm text-text-secondary font-body">Session error. Try refreshing the page.</p>
                </div>
            </div>
        );
    }

    return (
        <CurrentUserProvider user={currentUser}>
            <PodProvider>
                <ViewOptionsSync currentUserName={currentUser.name}>
                    {children}
                </ViewOptionsSync>
            </PodProvider>
        </CurrentUserProvider>
    );
}
